import { ReactNode, useState } from 'react'
import PageShell from '@/components/Common/PageShell'
import { DateRangeAccordion } from '@/components/DateRangeAccordion'
import RouteStatisticsReportTable from '@/components/RouteStatisticsReportTable'
import { requirePermission } from '@/utils/auth'
import { parseAndValidateDates, formatDateForTimestamp, formatDateForUrl } from '@/utils/datetime'
import { Text } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { getOperationReportRoutesOptions } from '@/api/main'
import { useSuspenseQuery } from '@tanstack/react-query'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

export const Route = createFileRoute('/route-statistics/$startDate/$endDate')({
  component: () => (
    <QueryBoundary
      loadingFallback={<RouteLoadingFallback />}
      errorTitle={i18next.t('failedToLoadRouteStatistics', { ns: 'statistics' })}
      errorMessage={i18next.t('unableToLoad', { ns: 'statistics' })}
      pageTitle={i18next.t('routeStatistics', { ns: 'statistics' })}
      fullWidth
      backButtonHref={ROUTE_PATH}
    >
      <RouteComponent />
    </QueryBoundary>
  ),
  beforeLoad: () => requirePermission('drive_statistics'),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingRouteStatistics', { ns: 'statistics' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoadRouteStatistics', { ns: 'statistics' })}
        message={error.message || i18next.t('unableToLoad', { ns: 'statistics' })}
      />
    </PageShell>
  ),
})

const ROUTE_PATH = '/route-statistics/$startDate/$endDate'

function RouteStatisticsLayout({
  children,
  startDate,
  endDate,
  onRangeSelectionStateChange,
}: {
  children: ReactNode
  startDate: Date
  endDate: Date
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('statistics')

  const navigateToDateRange = (newStartDate: Date, newEndDate: Date) => {
    navigate({
      to: ROUTE_PATH,
      params: {
        startDate: formatDateForUrl(newStartDate),
        endDate: formatDateForUrl(newEndDate),
      },
    })
  }

  return (
    <PageShell title={t('routeStatistics')} fullWidth>
      <DateRangeAccordion
        startDate={startDate}
        endDate={endDate}
        onRangeChange={navigateToDateRange}
        onRangeSelectionStateChange={onRangeSelectionStateChange}
      />
      {children}
    </PageShell>
  )
}

function RouteComponent() {
  const { t } = useTranslation(['statistics', 'common'])
  const [isRangeSelectionInProgress, setIsRangeSelectionInProgress] = useState(false)

  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  const { data: reportData } = useSuspenseQuery(
    getOperationReportRoutesOptions({
      path: {
        started_after: formatDateForTimestamp(startDate),
        finished_before: formatDateForTimestamp(dayjs(endDate).add(1, 'day').toDate()),
      },
    })
  )

  return (
    <RouteStatisticsLayout
      startDate={startDate}
      endDate={endDate}
      onRangeSelectionStateChange={setIsRangeSelectionInProgress}
    >
      {isRangeSelectionInProgress ? (
        <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
          {t('selectRangeToSeeData', { ns: 'common' })}
        </Text>
      ) : (
        <RouteStatisticsReportTable data={reportData?.routes || []} isLoading={false} error={null} />
      )}
    </RouteStatisticsLayout>
  )
}

function RouteLoadingFallback() {
  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  return (
    <RouteStatisticsLayout startDate={startDate} endDate={endDate}>
      <RouteStatisticsReportTable data={[]} isLoading={true} error={null} />
    </RouteStatisticsLayout>
  )
}
